/**
 * LayerPropertiesDialog — QGIS Layer Properties equivalent
 *
 * Provides a comprehensive property inspector for vector and raster layers with 5 tabs:
 * 1. Information / General: Name, geometry type, visibility, opacity, CRS, extent, feature count
 * 2. Symbology: Geometry-aware styling (Point markers, Line strokes, Polygon fill/outline, Raster opacity)
 * 3. Labels: Feature labeling configuration (field selector, font, size, color, halo, placement)
 * 4. Source / Metadata: Source format, URL/path, CRS parameters, spatial bounds
 * 5. Attributes / Fields: Field schema table, field types, sample values, link to Attribute Table
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Slider,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import {
  Info,
  Palette,
  Label as LabelIcon,
  Storage,
  TableChart,
  Close,
  RadioButtonUnchecked,
  Timeline,
  Pentagon,
  Image as ImageIcon,
  OpenInNew,
} from '@mui/icons-material';

import type { Layer } from '../store/slices/layersSlice';
import type { Symbology, LabelConfig, MarkerSymbol } from '../types/symbology';
import { getDefaultSymbology, DEFAULT_LABEL_CONFIG } from '../types/symbology';
import LabelConfigPanel from './LabelConfigPanel';
import SymbologyEditor from './SymbologyEditor';
import { useAppDispatch } from '../store/hooks';
import { updateLayer, updateLayerSymbology, updateLayerAsync } from '../store/slices/layersSlice';
import { setSidebarOpen, addNotification } from '../store/slices/uiSlice';

interface Props {
  open: boolean;
  layer: Layer | null;
  onClose: () => void;
  onApply?: (updates: Partial<Layer>) => void;
}

const GeometryBadge: React.FC<{ type?: string; layerType?: string }> = ({ type, layerType }) => {
  if (layerType === 'raster') {
    return (
      <Chip
        icon={<ImageIcon sx={{ fontSize: 16 }} />}
        label="Raster Layer"
        size="small"
        color="secondary"
        variant="outlined"
      />
    );
  }
  if (type === 'Point') {
    return (
      <Chip
        icon={<RadioButtonUnchecked sx={{ fontSize: 16 }} />}
        label="Point Vector"
        size="small"
        color="primary"
        variant="outlined"
      />
    );
  }
  if (type === 'LineString') {
    return (
      <Chip
        icon={<Timeline sx={{ fontSize: 16 }} />}
        label="LineString Vector"
        size="small"
        color="info"
        variant="outlined"
      />
    );
  }
  return (
    <Chip
      icon={<Pentagon sx={{ fontSize: 16 }} />}
      label="Polygon Vector"
      size="small"
      color="success"
      variant="outlined"
    />
  );
};

const ColorPickerInput: React.FC<{
  label: string;
  value: string;
  onChange: (color: string) => void;
}> = ({ label, value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1 }}>
    <Box
      component="input"
      type="color"
      value={value || '#3388ff'}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      sx={{
        width: 36,
        height: 36,
        padding: 0,
        border: '1px solid #555',
        borderRadius: '4px',
        cursor: 'pointer',
        backgroundColor: 'transparent',
      }}
    />
    <TextField
      size="small"
      label={label}
      value={value || '#3388ff'}
      onChange={(e) => onChange(e.target.value)}
      sx={{ width: 140 }}
    />
  </Box>
);

const LayerPropertiesDialog: React.FC<Props> = ({ open, layer, onClose, onApply }) => {
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState(0);

  // General state
  const [name, setName] = useState('');
  const [visible, setVisible] = useState(true);
  const [opacity, setOpacity] = useState(1);
  const [zIndex, setZIndex] = useState(1);

  // Symbology state
  const [symbology, setSymbology] = useState<Symbology | null>(null);
  const [showAdvancedSymbology, setShowAdvancedSymbology] = useState(false);

  // Labels state
  const [labelConfig, setLabelConfig] = useState<LabelConfig>(DEFAULT_LABEL_CONFIG);

  // Sync state whenever layer changes
  useEffect(() => {
    if (layer) {
      setName(layer.name || '');
      setVisible(layer.visible ?? true);
      setOpacity(layer.opacity ?? 1);
      setZIndex(layer.zIndex ?? 1);

      const geomType = (layer.geometryType as 'Point' | 'LineString' | 'Polygon') || 'Polygon';
      const initialSymbology = layer.symbology || getDefaultSymbology(geomType);
      setSymbology(initialSymbology);

      if (initialSymbology.labelConfig) {
        setLabelConfig(initialSymbology.labelConfig);
      } else {
        setLabelConfig({ ...DEFAULT_LABEL_CONFIG });
      }
    }
  }, [layer]);

  // Extract available attribute fields from feature properties
  const fields = useMemo(() => {
    if (!layer?.features || layer.features.length === 0) return [];
    const keySet = new Set<string>();
    layer.features.forEach((f) => {
      if (f.properties) {
        Object.keys(f.properties).forEach((k) => keySet.add(k));
      }
    });
    return Array.from(keySet);
  }, [layer]);

  // Extract schema details for Attributes tab
  const fieldSchemas = useMemo(() => {
    if (!layer?.features || layer.features.length === 0) return [];
    const map = new Map<string, { name: string; type: string; sample: string }>();

    layer.features.forEach((f) => {
      if (!f.properties) return;
      Object.entries(f.properties).forEach(([key, val]) => {
        if (!map.has(key)) {
          let valType = typeof val;
          if (val === null || val === undefined) valType = 'unknown';
          else if (Array.isArray(val)) valType = 'Array';
          else if (valType === 'number') valType = Number.isInteger(val) ? 'Integer' : 'Float';
          else if (valType === 'string') valType = 'String';
          else if (valType === 'boolean') valType = 'Boolean';

          map.set(key, {
            name: key,
            type: valType,
            sample: val !== null && val !== undefined ? String(val).slice(0, 40) : 'null',
          });
        }
      });
    });

    return Array.from(map.values());
  }, [layer]);

  // Calculate Spatial Bounding Box / Extent
  const extent = useMemo(() => {
    if (!layer?.features || layer.features.length === 0) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    const processCoords = (coords: any) => {
      if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        minX = Math.min(minX, coords[0]);
        minY = Math.min(minY, coords[1]);
        maxX = Math.max(maxX, coords[0]);
        maxY = Math.max(maxY, coords[1]);
      } else if (Array.isArray(coords)) {
        coords.forEach(processCoords);
      }
    };

    layer.features.forEach((f) => {
      if (f.geometry && f.geometry.coordinates) {
        processCoords(f.geometry.coordinates);
      }
    });

    if (minX === Infinity) return null;
    return {
      minX: minX.toFixed(5),
      minY: minY.toFixed(5),
      maxX: maxX.toFixed(5),
      maxY: maxY.toFixed(5),
    };
  }, [layer]);

  if (!layer) return null;

  const geomType = (layer.geometryType as 'Point' | 'LineString' | 'Polygon') || 'Polygon';

  const handleSave = () => {
    const nextSymbology = symbology
      ? { ...symbology, labelConfig }
      : { ...getDefaultSymbology(geomType), labelConfig };

    const updates: Partial<Layer> = {
      name,
      visible,
      opacity,
      zIndex,
      symbology: nextSymbology,
    };

    dispatch(updateLayer({ id: layer.id, updates }));
    dispatch(updateLayerSymbology({ layerId: layer.id, symbology: nextSymbology }));
    dispatch(updateLayerAsync({ id: layer.id, updates }));

    if (onApply) {
      onApply(updates);
    }

    dispatch(addNotification({ type: 'success', message: `Layer "${name}" properties updated` }));
    onClose();
  };

  const handleOpenAttributeTable = () => {
    window.dispatchEvent(
      new CustomEvent('geovera:open-attribute-table', { detail: { layerId: layer.id } })
    );
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2, minHeight: 520 } } }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Layer Properties — {layer.name}
            </Typography>
            <GeometryBadge type={layer.geometryType} layerType={layer.type} />
          </Box>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<Info sx={{ fontSize: 18 }} />} iconPosition="start" label="General" />
            <Tab icon={<Palette sx={{ fontSize: 18 }} />} iconPosition="start" label="Symbology" />
            <Tab icon={<LabelIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Labels" />
            <Tab icon={<Storage sx={{ fontSize: 18 }} />} iconPosition="start" label="Source" />
            <Tab
              icon={<TableChart sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={`Attributes (${fieldSchemas.length})`}
            />
          </Tabs>
        </Box>

        <DialogContent sx={{ minHeight: 380, pt: 2.5 }}>
          {/* ────────────────── 1. GENERAL TAB ────────────────── */}
          {tab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Layer Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="outlined"
                size="small"
              />

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch checked={visible} onChange={(e) => setVisible(e.target.checked)} />
                  }
                  label="Visible on Map"
                />
              </Box>

              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Opacity: {Math.round(opacity * 100)}%
                </Typography>
                <Slider
                  value={opacity}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(_, val) => setOpacity(val as number)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  type="number"
                  label="Z-Index (Stacking)"
                  value={zIndex}
                  onChange={(e) => setZIndex(Number(e.target.value))}
                  size="small"
                  sx={{ width: 160 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Higher z-index layers render above lower ones on the map.
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Layer Summary
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.02)' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Type:</strong> {layer.type || 'vector'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Geometry:</strong> {layer.geometryType || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Feature Count:</strong>{' '}
                    {layer.features?.length ?? layer.featureCount ?? 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>CRS:</strong> {layer.crs || 'EPSG:4326 (WGS 84)'}
                  </Typography>
                </Box>
                {extent && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      <strong>Spatial Extent (Bounding Box):</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      [{extent.minX}, {extent.minY}] to [{extent.maxX}, {extent.maxY}]
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {/* ────────────────── 2. SYMBOLOGY TAB ────────────────── */}
          {tab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {geomType} Layer Styling
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Palette />}
                  onClick={() => setShowAdvancedSymbology(true)}
                >
                  Advanced Symbology Editor
                </Button>
              </Box>

              {layer.type === 'raster' ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Raster layer styling controls opacity and color rendering.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Opacity: {Math.round(opacity * 100)}%
                  </Typography>
                  <Slider
                    value={opacity}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={(_, val) => setOpacity(val as number)}
                  />
                </Box>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  {/* Point Controls */}
                  {geomType === 'Point' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControl size="small" sx={{ width: 200 }}>
                        <InputLabel>Marker Symbol</InputLabel>
                        <Select
                          value={symbology?.markerSymbol || 'circle'}
                          label="Marker Symbol"
                          onChange={(e) =>
                            setSymbology((prev) => ({
                              ...getDefaultSymbology('Point'),
                              ...prev,
                              markerSymbol: e.target.value as MarkerSymbol,
                            }))
                          }
                        >
                          <MenuItem value="circle">Circle</MenuItem>
                          <MenuItem value="square">Square</MenuItem>
                          <MenuItem value="triangle">Triangle</MenuItem>
                          <MenuItem value="star">Star</MenuItem>
                          <MenuItem value="cross">Cross (+)</MenuItem>
                          <MenuItem value="x">X mark</MenuItem>
                        </Select>
                      </FormControl>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Marker Size: {symbology?.markerSize || 8}px
                        </Typography>
                        <Slider
                          value={symbology?.markerSize || 8}
                          min={3}
                          max={30}
                          onChange={(_, val) =>
                            setSymbology((prev) => ({
                              ...getDefaultSymbology('Point'),
                              ...prev,
                              markerSize: val as number,
                            }))
                          }
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <ColorPickerInput
                          label="Fill Color"
                          value={symbology?.fillColor || '#3388ff'}
                          onChange={(color) =>
                            setSymbology((prev) => ({
                              ...getDefaultSymbology('Point'),
                              ...prev,
                              fillColor: color,
                            }))
                          }
                        />
                        <ColorPickerInput
                          label="Stroke Color"
                          value={symbology?.strokeColor || '#ffffff'}
                          onChange={(color) =>
                            setSymbology((prev) => ({
                              ...getDefaultSymbology('Point'),
                              ...prev,
                              strokeColor: color,
                            }))
                          }
                        />
                      </Box>
                    </Box>
                  )}

                  {/* LineString Controls */}
                  {geomType === 'LineString' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <ColorPickerInput
                        label="Line Color"
                        value={symbology?.strokeColor || '#3388ff'}
                        onChange={(color) =>
                          setSymbology((prev) => ({
                            ...getDefaultSymbology('LineString'),
                            ...prev,
                            strokeColor: color,
                          }))
                        }
                      />

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Line Width: {symbology?.strokeWidth || 3}px
                        </Typography>
                        <Slider
                          value={symbology?.strokeWidth || 3}
                          min={1}
                          max={15}
                          onChange={(_, val) =>
                            setSymbology((prev) => ({
                              ...getDefaultSymbology('LineString'),
                              ...prev,
                              strokeWidth: val as number,
                            }))
                          }
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Polygon Controls */}
                  {geomType === 'Polygon' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <ColorPickerInput
                          label="Fill Color"
                          value={symbology?.fillColor || '#3388ff'}
                          onChange={(color) =>
                            setSymbology((prev) => ({
                              ...getDefaultSymbology('Polygon'),
                              ...prev,
                              fillColor: color,
                            }))
                          }
                        />
                        <ColorPickerInput
                          label="Outline Color"
                          value={symbology?.strokeColor || '#ffffff'}
                          onChange={(color) =>
                            setSymbology((prev) => ({
                              ...getDefaultSymbology('Polygon'),
                              ...prev,
                              strokeColor: color,
                            }))
                          }
                        />
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Outline Width: {symbology?.strokeWidth || 2}px
                        </Typography>
                        <Slider
                          value={symbology?.strokeWidth || 2}
                          min={0}
                          max={10}
                          onChange={(_, val) =>
                            setSymbology((prev) => ({
                              ...getDefaultSymbology('Polygon'),
                              ...prev,
                              strokeWidth: val as number,
                            }))
                          }
                        />
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          )}

          {/* ────────────────── 3. LABELS TAB ────────────────── */}
          {tab === 2 && (
            <Box>
              {fields.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No feature attribute fields available to label for this layer.
                </Typography>
              ) : (
                <LabelConfigPanel
                  fields={fields}
                  currentConfig={labelConfig}
                  geometryType={geomType}
                  onApply={(cfg) => setLabelConfig(cfg)}
                  onCancel={() => undefined}
                />
              )}
            </Box>
          )}

          {/* ────────────────── 4. SOURCE / METADATA TAB ────────────────── */}
          {tab === 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Data Source & Metadata
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" paragraph>
                  <strong>Storage Provider:</strong>{' '}
                  {layer.type === 'raster' ? 'Raster Tile Server' : 'PostGIS Feature Store'}
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Layer Identifier:</strong> <code>{layer.id}</code>
                </Typography>

                {(layer as any).sourceUrl && (
                  <Typography variant="body2" paragraph sx={{ wordBreak: 'break-all' }}>
                    <strong>Source URL:</strong> {(layer as any).sourceUrl}
                  </Typography>
                )}

                <Typography variant="body2" paragraph>
                  <strong>Coordinate System:</strong> {layer.crs || 'EPSG:4326 (WGS 84)'}
                </Typography>

                <Typography variant="body2">
                  <strong>Total Loaded Features:</strong> {layer.features?.length ?? 0}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* ────────────────── 5. ATTRIBUTES / FIELDS TAB ────────────────── */}
          {tab === 4 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Feature Attribute Fields ({fieldSchemas.length})
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<OpenInNew />}
                  onClick={handleOpenAttributeTable}
                >
                  Open Attribute Table
                </Button>
              </Box>

              {fieldSchemas.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No feature attributes found in this layer.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Data Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Sample Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fieldSchemas.map((field) => (
                        <TableRow key={field.name} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                            {field.name}
                          </TableCell>
                          <TableCell>
                            <Chip label={field.type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                            {field.sample}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Apply & Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advanced Symbology Modal */}
      {showAdvancedSymbology && (
        <SymbologyEditor
          open={showAdvancedSymbology}
          layerName={layer.name}
          geometryType={geomType}
          currentSymbology={symbology || getDefaultSymbology(geomType)}
          onApply={(updatedSymbology) => {
            setSymbology(updatedSymbology);
            setShowAdvancedSymbology(false);
          }}
          onCancel={() => setShowAdvancedSymbology(false)}
        />
      )}
    </>
  );
};

export default LayerPropertiesDialog;
